/* Qorvex — shared types.
 *
 * Money and quantity are STRINGS end to end. A JavaScript number is an IEEE-754
 * double and cannot represent 0.05 exactly, so parsing a price into a number
 * and formatting it back can change it. Values stay as strings and are parsed
 * only for display or comparison.
 */

export type TradingMode = 'BACKTEST' | 'PAPER' | 'LIVE'
export type OrderSide = 'BUY' | 'SELL'
export type OrderType =
  | 'MARKET' | 'LIMIT' | 'STOP' | 'STOP_LIMIT'
  | 'MARKET_ON_CLOSE' | 'LIMIT_ON_CLOSE' | 'PEGGED'
export type TimeInForce = 'DAY' | 'IOC' | 'FOK' | 'GTC' | 'GTD' | 'ATO' | 'ATC'
export type ProductType = 'CNC' | 'MIS' | 'NRML' | 'CO' | 'BO' | 'MARGIN' | 'CASH'

export type OrderStatus =
  | 'CREATED' | 'VALIDATED' | 'RISK_CHECKED' | 'SUBMITTED' | 'ACKNOWLEDGED'
  | 'PARTIALLY_FILLED' | 'FILLED' | 'CANCELLED' | 'REJECTED' | 'EXPIRED'
  | 'PENDING_CANCEL' | 'PENDING_AMEND' | 'REPLACED'

/** Traditional regulated instrument types only. There is deliberately no
 *  cryptocurrency or digital-asset member. */
export type AssetClass =
  | 'EQUITY' | 'ETF' | 'INDEX'
  | 'EQUITY_FUTURE' | 'EQUITY_OPTION'
  | 'INDEX_FUTURE' | 'INDEX_OPTION'
  | 'COMMODITY_FUTURE' | 'COMMODITY_OPTION'
  | 'CURRENCY_FUTURE' | 'CURRENCY_OPTION'
  | 'FX_SPOT' | 'BOND' | 'MUTUAL_FUND'

export interface Quote {
  instrument: number | string
  /** Instrument-master UUID, when known — what the order API expects. */
  instrumentId?: string
  symbol?: string
  exchange?: string
  last: string
  bid: string
  ask: string
  bidQty?: string
  askQty?: string
  volume: string
  open?: string
  high?: string
  low?: string
  prevClose?: string
  change?: string
  changePct?: string
  vwap?: string
  openInterest?: string
  stale: boolean
  ts?: number
}

export interface Order {
  id: string
  cl_ord_id: string
  venue_order_id?: string | null
  account_id: string
  instrument_id: string
  symbol?: string
  exchange?: string
  trading_mode: TradingMode
  side: OrderSide
  order_type: OrderType
  time_in_force: TimeInForce
  product: ProductType
  quantity: string
  filled_quantity: string
  remaining_quantity: string
  price?: string | null
  trigger_price?: string | null
  avg_fill_price?: string | null
  status: OrderStatus
  reject_code?: string | null
  reject_reason?: string | null
  currency: string
  realized_fees: string
  created_at: string
}

export interface Position {
  id: string
  account_id: string
  instrument_id: string
  symbol?: string
  exchange?: string
  product: ProductType
  trading_mode: TradingMode
  quantity: string
  avg_price: string
  last_price?: string | null
  market_value: string
  realized_pnl: string
  unrealized_pnl: string
  total_pnl: string
  exposure: string
  margin_used: string
  currency: string
}

export interface RiskWarning {
  code: string
  severity: 'INFO' | 'WARNING' | 'HIGH' | 'CRITICAL'
  message: string
  limit_value?: string | null
  projected_value?: string | null
}

export interface OrderPreview {
  would_be_accepted: boolean
  estimated_margin: string
  estimated_fees: string
  estimated_exposure: string
  notional_value: string
  resulting_position: string
  margin_after: string
  margin_utilization_pct: string
  warnings: RiskWarning[]
  blocking_reason?: string | null
  fee_breakdown: Record<string, string>
}

export interface Bar {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

export interface ConnectionHealth {
  api: 'connected' | 'connecting' | 'disconnected'
  engine: 'healthy' | 'degraded' | 'unavailable'
  marketData: 'live' | 'stale' | 'disconnected'
  lastUpdate: number
}

export interface Execution {
  id: string
  order_id: string
  instrument_id: string
  symbol?: string
  trading_mode: TradingMode
  side: OrderSide
  quantity: string
  price: string
  gross_value: string
  total_costs: string
  net_value: string
  currency: string
  exec_time: string
}

export interface TradePrint {
  ts: number
  price: string
  size: string
  aggressor: OrderSide
}

export interface DepthLevel {
  price: string
  qty: string
}

export interface Depth {
  bids: DepthLevel[]
  asks: DepthLevel[]
}

/** A strategy as the engine reports it over the control socket. */
export interface StrategyStatus {
  id: number
  name: string
  state: 'STOPPED' | 'STARTING' | 'RUNNING' | 'PAUSED' | 'QUARANTINED' | 'FAILED'
  account: number
  mode: TradingMode
  orders_submitted: number
  orders_rejected: number
  bars: number
  exceptions: number
  fault: string
}

// ---------------------------------------------------------------------------
// Market-data views for the terminal
// ---------------------------------------------------------------------------

export interface DepthRow {
  price: string
  quantity: number
  orders: number
  level: number
  /** True when this level was CONSTRUCTED from the quoted spread rather than
   *  observed. A derived level is not resting liquidity and must be labelled
   *  as such wherever it is shown. */
  derived: boolean
}

export interface MarketDepth {
  symbol: string
  exchange: string
  instrument_id: string
  name?: string
  tick_size?: string
  lot_size?: string
  last?: string
  bid?: string
  ask?: string
  spread?: string
  bids: DepthRow[]
  asks: DepthRow[]
  total_bid_quantity?: number
  total_ask_quantity?: number
  imbalance_pct?: number
  quote_source?: string
  /** VENUE = real exchange depth. DERIVED = built from a top-of-book quote.
   *  NONE = no quote at all. */
  depth_source: 'VENUE' | 'DERIVED' | 'NONE'
  note?: string
  as_of?: string
}

export interface Mover {
  instrument_id: string
  symbol: string
  name: string
  sector: string | null
  last: string
  prev_close: string
  change: string
  change_pct: number
  source: string
}

export interface MoversResponse {
  exchange: string
  gainers: Mover[]
  losers: Mover[]
  universe: number
  quoted: number
  unquoted: number
  note: string
  as_of: string
}

export interface SectorPerformance {
  sector: string
  constituents: number
  avg_change_pct: number
  advancing: number
  declining: number
  members: Mover[]
}

export interface IndexQuote {
  instrument_id: string
  symbol: string
  name: string
  exchange: string
  last: string | null
  source: string | null
  quoted: boolean
}

export interface Greeks {
  delta: number | null
  gamma: number | null
  theta: number | null
  vega: number | null
  rho: number | null
  computable: boolean
}

export interface OptionLeg {
  instrument_id: string
  symbol: string
  lot_size: string
  tick_size: string
  last: string | null
  bid: string | null
  ask: string | null
  quote_source: string | null
  greeks: Greeks
}

export interface OptionStrike {
  strike: string
  call: OptionLeg | null
  put: OptionLeg | null
}

export interface OptionChain {
  underlying: string
  underlying_name: string
  spot: string | null
  spot_source?: string | null
  expiries: string[]
  expiry: string | null
  days_to_expiry?: number
  atm_strike?: string | null
  strikes: OptionStrike[]
  greeks_basis?: {
    model: string
    computed: boolean
    assumed_volatility: number
    risk_free_rate: number
    note: string
  }
  note?: string
  as_of?: string
}

// ---------------------------------------------------------------------------
// HFT telemetry (architecture boxes 3, 4, 5, 11, 12)
// ---------------------------------------------------------------------------

export interface FeedHandlerTelemetry {
  enabled: boolean
  venue?: string
  state?: string
  packets_a?: number
  packets_b?: number
  duplicates_discarded?: number
  processed?: number
  messages_decoded?: number
  gaps_detected?: number
  packets_lost?: number
  retransmits_requested?: number
  snapshots_requested?: number
  heartbeat_timeouts?: number
  oversize?: number
  ring_full_drops?: number
  unmapped_instrument?: number
  truncated?: number
  bad_length?: number
  last_sequence?: number
  gap_state?: string
  a_lead_ns?: number
  redundant?: boolean
  redundancy_note?: string | null
  note?: string
}

export interface SubscriberTelemetry {
  name: string
  class: string
  published: number
  dropped: number
  backlog: number
  capacity: number
  loss_pct: number
}

export interface DistributorTelemetry {
  ticks_in?: number
  fanouts?: number
  drops?: number
  subscribers?: number
  shared_bus?: boolean
  shm_published?: number
  conflated_collapsed?: number
}

export interface ClockTelemetry {
  enabled?: boolean
  state?: string
  certified?: boolean
  timestamps_certified?: boolean
  offset_ns?: number
  path_delay_ns?: number
  source?: string
  samples?: number
  lock_losses?: number
  max_offset_ns?: number
  note?: string | null
}

export interface ReplicaTelemetry {
  enabled?: boolean
  id?: string
  role?: 'ACTIVE' | 'STANDBY' | 'DEMOTED' | 'ISOLATED' | string
  may_send_orders?: boolean
  fencing_token?: number
  peer_id?: string
  peer_token?: number
  peer_alive?: boolean
  state_lag?: number
  promotions?: number
  demotions?: number
  reason?: string
  warning?: string | null
}

export interface ComponentTelemetry {
  name: string
  state: 'HEALTHY' | 'DEGRADED' | 'FAILED' | 'QUARANTINED' | 'STOPPED' | string
  critical: boolean
  beats: number
  missed: number
  since_beat_ms: number
  restarts: number
  detail: string
}

export interface HealthTelemetry {
  worst?: string
  healthy?: number
  degraded?: number
  failed?: number
  quarantined?: number
  critical_failures?: number
  trading_permitted?: boolean
  alerts?: number
  recoveries_attempted?: number
  recoveries_succeeded?: number
}

export interface AcceleratorTelemetry {
  device?: string
  vendor?: string
  hardware?: boolean
  stages?: number
  packets?: number
  filtered?: number
  signals?: number
  risk_precheck_blocks?: number
  measured_wire_to_signal_ns?: number
}

export interface HftTelemetry {
  ok: boolean
  engine?: string
  error?: string
  note?: string
  feed_handler?: FeedHandlerTelemetry
  distributor?: DistributorTelemetry
  subscribers?: SubscriberTelemetry[]
  time_sync?: ClockTelemetry
  replica?: ReplicaTelemetry
  accelerator?: AcceleratorTelemetry
  health?: HealthTelemetry
  components?: ComponentTelemetry[]
}

export interface LatencyStage {
  stage: string
  n: number
  p50: number
  p95: number
  p99: number
  p999: number
  max: number
}

export interface LatencyReport {
  ok: boolean
  measured?: boolean
  stages?: LatencyStage[]
  units?: string
  note?: string
  error?: string
}

export interface AccountBalances {
  available_margin?: string
  used_margin?: string
  total_margin?: string
  cash?: string
  currency?: string
  [k: string]: unknown
}

// ---------------------------------------------------------------------------
// GTT — resting trigger instructions
//
// A GTT is NOT an order. It holds no margin, occupies no venue slot and may
// rest for a year. When a leg's condition is met the evaluator places a normal
// order, validated and risk-checked at THAT moment.
// ---------------------------------------------------------------------------

export type GttStatus =
  | 'ACTIVE' | 'TRIGGERED' | 'CANCELLED' | 'EXPIRED' | 'REJECTED' | 'FAILED'
export type GttKind = 'SINGLE' | 'OCO'
export type GttCondition = 'LAST_ABOVE' | 'LAST_BELOW'
export type GttLabel = 'ENTRY' | 'STOP_LOSS' | 'TARGET'

export interface GttLeg {
  id: string
  label: GttLabel
  condition: GttCondition
  trigger_price: string
  side: OrderSide
  order_type: 'LIMIT' | 'MARKET'
  quantity: string
  limit_price: string | null
  fired: boolean
  triggered_order_id: string | null
  triggered_at: string | null
}

export interface Gtt {
  id: string
  account_id: string
  instrument_id: string
  symbol: string
  exchange: string
  kind: GttKind
  status: GttStatus
  product: ProductType
  trading_mode: TradingMode
  reference_price: string | null
  valid_until: string
  notes: string | null
  created_at: string
  triggered_at: string | null
  cancelled_at: string | null
  cancel_reason: string | null
  legs: GttLeg[]
}

export interface GttCreated {
  id: string
  kind: GttKind
  status: GttStatus
  symbol: string
  exchange?: string
  reference_price: string | null
  quote_source?: string | null
  /** True when the instrument is priced by the simulator rather than a market
   *  feed. A trigger judged against a random walk fires for no market reason. */
  simulated_price?: boolean
  valid_until: string
  legs: number
  note: string
}

/** Whether anything is actually watching resting instructions.
 *
 *  This matters more than it looks: a GTT list that renders while no evaluator
 *  runs is a screen full of protection that does not exist. */
export interface GttEvaluatorStatus {
  running: boolean
  poll_seconds: number
  started_at: string | null
  last_pass_at: string | null
  passes: number
  instructions_evaluated: number
  legs_triggered: number
  expired: number
  failed: number
  skipped_no_quote: number
  blocked_simulated_price: number
  last_error: string | null
  note: string
}

// ---------------------------------------------------------------------------
// Quant — strategies, rule graphs and backtests
// ---------------------------------------------------------------------------

/** An instrument-master row, as GET /instruments returns it. */
export interface Instrument {
  id: string
  symbol: string
  exchange: string
  name: string
  asset_class: string
  segment: string
  currency: string
  sector: string | null
  status: string
  lot_size: string
  tick_size: string
  multiplier: string
  max_order_qty: string | null
  /** NULL means the trading engine does not serve this instrument: it can be
   *  backtested against stored history but NOT deployed. */
  engine_instrument_id: number | null
}

/** The operands the engine's rule evaluator understands. Indicator periods are
 *  FIXED in the engine's indicator bank (SMA 20, EMA 200, RSI 14, MACD 12/26/9,
 *  Bollinger 20/2, ATR 14, ADX 14, Stoch 14/3, Supertrend 10/3). */
export const RULE_OPERANDS = [
  'CLOSE', 'OPEN', 'HIGH', 'LOW', 'VOLUME',
  'SMA', 'EMA', 'VWAP', 'RSI', 'MACD', 'MACD_SIGNAL', 'MACD_HIST',
  'BB_UPPER', 'BB_MIDDLE', 'BB_LOWER', 'ATR', 'ADX', 'STOCH_K', 'STOCH_D',
  'SUPERTREND', 'AVG_VOLUME', 'POSITION', 'PNL_PCT', 'CONSTANT',
] as const
export type RuleOperand = (typeof RULE_OPERANDS)[number]

export const RULE_OPS = [
  '<', '<=', '>', '>=', '==', '!=', 'CROSSES_ABOVE', 'CROSSES_BELOW',
] as const
export type RuleOp = (typeof RULE_OPS)[number]

/** One comparison: left against either another operand (right) or a
 *  number (value) — exactly one of the two. */
export interface RuleCondition {
  left: RuleOperand
  op: RuleOp
  right?: RuleOperand
  value?: number
}

export interface RuleSet {
  combinator: 'AND' | 'OR'
  conditions: RuleCondition[]
}

/** The rule-graph JSON stored via POST /strategies/{id}/versions and loaded
 *  into the engine on deploy. The shape is the engine's grammar — do not
 *  extend it from the UI. */
export interface RuleGraph {
  /** ENGINE instrument ids (ints), not instrument-master UUIDs. */
  instruments: number[]
  entry: RuleSet
  exit: RuleSet
  risk: {
    position_size: number
    max_position: number
    stop_loss_pct: number
    take_profit_pct: number
    trailing_stop_pct: number
  }
}

/** A strategy as GET /strategies returns it. */
export interface Strategy {
  id: string
  code: string
  name: string
  description: string | null
  language: string
  status: string
  approved_for_live: boolean
  current_version: number | null
  created_at: string
}

/** One row of GET /backtests. Metrics are decimal STRINGS (fractions, not
 *  percentages: 0.12 means +12%) and may be null when not computed. */
export interface BacktestSummary {
  id: string
  name: string
  status: string
  start_date: string
  end_date: string
  initial_capital: string
  total_return: string | null
  cagr: string | null
  sharpe_ratio: string | null
  max_drawdown: string | null
  win_rate: string | null
  trades_count: number | null
  completed_at: string | null
}

export interface EquityPoint {
  /** epoch milliseconds */
  t: number
  equity: number
}

/** The full report from GET /backtests/{id}/report. Ratio and return metrics
 *  are decimal strings; fractions where noted. methodology_notes,
 *  limitations_notes and disclaimer are part of the result and must always be
 *  rendered with it. */
export interface BacktestReport {
  id: string
  name: string
  status: string
  start_date: string
  end_date: string
  initial_capital: string
  currency: string
  data_resolution: string
  total_return: string | null
  cagr: string | null
  sharpe_ratio: string | null
  sortino_ratio: string | null
  max_drawdown: string | null
  max_drawdown_days: number | null
  win_rate: string | null
  profit_factor: string | null
  expectancy: string | null
  volatility: string | null
  trades_count: number | null
  total_costs: string | null
  equity_curve: EquityPoint[] | null
  monthly_returns: Record<string, number> | null
  methodology_notes: string | null
  limitations_notes: string | null
  disclaimer: string
  result_class: string
  error_message: string | null
  completed_at: string | null
  [k: string]: unknown
}

/** One row of GET /backtests/{id}/trades. */
export interface BacktestTradeRow {
  seq: number
  symbol: string
  side: OrderSide
  quantity: string
  entry_time: string
  entry_price: string
  exit_time: string | null
  exit_price: string | null
  gross_pnl: string | null
  costs: string
  net_pnl: string | null
  exit_reason: string | null
}

// ---------------------------------------------------------------------------
// Simulator — day-trading practice sessions
//
// Every simulator response carries result_type SIMULATION. Practice results
// are never merged with backtest, paper or live figures.
// ---------------------------------------------------------------------------

export type SimMode = 'REALTIME' | 'REPLAY'
export type SimDataSource = 'RANDOM_WALK' | 'ALPACA' | 'REPLAY' | 'UPSTOX_NSE'
export type SimOrderType = 'MARKET' | 'LIMIT' | 'STOP' | 'STOP_LIMIT'

export interface SimSession {
  id: string
  name: string
  mode: SimMode
  data_source: SimDataSource
  status: string
  starting_cash: string
  cash: string
  realized_pnl: string
  commission_paid: string
  buying_power_mult: string
  commission_per_share: string
  slippage_bps: string
  max_daily_loss: string | null
  max_position_qty: string | null
  reset_count: number
  created_at: string
  closed_at: string | null
  result_type: string
}

export interface SimOrder {
  id: string
  session_id: string
  symbol: string
  side: OrderSide
  order_type: SimOrderType
  quantity: string
  limit_price: string | null
  stop_price: string | null
  status: string
  filled_quantity: string
  avg_fill_price: string | null
  commission: string
  reject_reason: string | null
  created_at: string
  filled_at: string | null
  result_type: string
}

export interface SimPosition {
  symbol: string
  quantity: string
  avg_price: string
  realized_pnl: string
  last_price: string | null
  unrealized_pnl: string | null
  market_value: string | null
  result_type: string
}

export interface SimSnapshot {
  session: SimSession
  positions: SimPosition[]
  equity: string
  buying_power: string
  unrealized_pnl: string
  total_pnl: string
  open_orders: number
  filled_orders: number
}

export interface SimQuote {
  symbol: string
  bid: string
  ask: string
  last: string
  source: string
  as_of: string
}

// ===========================================================================
// Algo products — Atlas (marketplace) and Forge (strategy building)
// ===========================================================================

/** The published record on a listing.
 *
 *  `result_class` and the period are NOT optional by accident: the API refuses
 *  to publish a listing whose metrics cannot say what kind of result they are
 *  and over what window, so a client never has to render a bare number with no
 *  provenance. */
export interface PublishedMetrics {
  result_class: 'BACKTEST' | 'PAPER' | 'LIVE'
  period_start: string
  period_end: string
  backtest_id?: string
  total_return?: string | null
  cagr?: string | null
  sharpe_ratio?: string | null
  max_drawdown?: string | null
  win_rate?: string | null
  trades_count?: number | null
  initial_capital?: string | null
  currency?: string | null
  methodology?: string | null
  limitations?: string | null
  computed_at?: string | null
}

export interface AtlasListing {
  id: string
  slug: string
  title: string
  summary: string
  long_description?: string | null
  tier: 'OPEN' | 'EDGE'
  status: 'DRAFT' | 'PUBLISHED' | 'PAUSED' | 'RETIRED'
  category?: string | null
  risk_grade: 'CONSERVATIVE' | 'BALANCED' | 'AGGRESSIVE'
  asset_classes: string[]
  exchanges: string[]
  symbols: string[]
  min_capital: string
  currency: string
  fee_model: Record<string, unknown>
  published_metrics: Partial<PublishedMetrics>
  disclosure: string
  subscriber_count: number
  strategy_id: string
  strategy_version_id: string
  strategy_code?: string | null
  strategy_version?: number | null
  published_at?: string | null
  /** OPEN (whitebox) listings publish their graph; EDGE listings return null. */
  rule_graph?: RuleGraph | null
  subscribed: boolean
}

export interface AtlasSubscription {
  id: string
  listing_id: string
  status: 'ACTIVE' | 'CANCELLED'
  subscribed_at: string
  cancelled_at?: string | null
  strategy_run_id?: string | null
  last_deployed_mode?: string | null
  account_id?: string | null
  slug: string
  title: string
  tier: 'OPEN' | 'EDGE'
  risk_grade: string
  strategy_id: string
  strategy_version: number
  run_status?: string | null
}

/** One phrase the drafter read, and what it turned into. */
export interface CopilotInterpretation {
  phrase: string
  section: 'instruments' | 'entry' | 'exit' | 'risk'
  understood: string
}

export interface CopilotDraft {
  id: string
  prompt: string
  rule_graph: RuleGraph
  interpretation: CopilotInterpretation[]
  /** What the drafter could NOT read, and which default it applied instead. */
  unresolved: string[]
  generator: string
  status: 'DRAFT' | 'ACCEPTED' | 'DISCARDED'
  strategy_id?: string | null
  accepted_at?: string | null
  created_at: string
}

export interface CopilotDraftResponse {
  draft_id: string
  created_at: string
  rule_graph: RuleGraph
  interpretation: CopilotInterpretation[]
  unresolved: string[]
  symbols: string[]
  generator: string
  note: string
}

export type BuildRequestStatus =
  | 'SUBMITTED' | 'IN_REVIEW' | 'CLARIFYING' | 'BUILDING'
  | 'DELIVERED' | 'REJECTED' | 'WITHDRAWN'

export interface ForgeBuildRequest {
  id: string
  title: string
  rules_text: string
  symbols: string[]
  target_lane: 'CLASSIC' | 'COPILOT' | 'PRO' | 'PYTHON'
  indicative_capital?: string | null
  risk_notes?: string | null
  status: BuildRequestStatus
  desk_notes?: string | null
  delivered_strategy_id?: string | null
  delivered_at?: string | null
  assigned_to?: string | null
  created_at: string
  updated_at: string
  requested_by: string
}

export interface ForgeBlocks {
  operands: { code: RuleOperand; label: string; group: string }[]
  operators: { code: RuleOp; label: string }[]
  note: string
}
